/*
 * Capital Raise Readiness Assessment — front-end controller
 * Version 1.0
 *
 * Mounts into #chadmc-capital-assessment (see sections/capital-raise-assessment.liquid).
 * Depends on capital-raise-assessment-config.js and capital-raise-assessment-scoring.js
 * being loaded first as globals (window.ChadMCAssessmentConfig / window.ChadMCAssessmentScoring).
 *
 * ---------------------------------------------------------------------------
 * BACKEND / KLAVIYO HANDOFF FOR MUSE — read this before wiring integrations.
 * ---------------------------------------------------------------------------
 * This file owns the UI and produces a structured lead+assessment payload
 * (see buildResultPayload below). It does NOT talk to Klaviyo and contains
 * no private credentials, per spec section 23.
 *
 * The integration point is window.ChadMCAssessment.integrations.submitLead —
 * a single async function Muse should replace/implement. It currently:
 *   1. Always persists the payload to localStorage (capital_assessment_leads
 *      queue) FIRST, so a lead is never lost even if submission fails.
 *   2. Dispatches a `chadmc:capital_assessment_lead_submit` CustomEvent on
 *      `document` with the full payload in `event.detail`, for any listener
 *      (GTM, a Klaviyo snippet, etc.) that prefers to hook in that way
 *      instead of editing this file.
 *   3. Calls the pluggable `window.ChadMCAssessment.integrations.submitLead`
 *      function and awaits it; on rejection, the payload simply stays queued
 *      in localStorage (see retryQueuedLeads) rather than being dropped.
 *
 * To wire real Klaviyo delivery, Muse should either:
 *   (a) replace `window.ChadMCAssessment.integrations.submitLead` with a
 *       function that POSTs to a Shopify app proxy endpoint (recommended —
 *       keeps any private API key server-side), or
 *   (b) listen for the `chadmc:capital_assessment_lead_submit` DOM event and
 *       handle delivery entirely independently of this file.
 * Do not put a private Klaviyo API key in this file or any other
 * browser-loaded asset.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var CONFIG = window.ChadMCAssessmentConfig;
  var SCORING = window.ChadMCAssessmentScoring;

  if (!CONFIG || !SCORING) {
    return; // config/scoring failed to load; fail silent rather than break the page
  }

  var STORAGE_KEY_PROGRESS = 'chadmc_capital_assessment_progress_v1';
  var STORAGE_KEY_HISTORY = 'chadmc_capital_assessment_history_v1';
  var STORAGE_KEY_LEAD_QUEUE = 'chadmc_capital_assessment_lead_queue_v1';

  /* ---------------- small utilities ---------------- */

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    attrs = attrs || {};
    Object.keys(attrs).forEach(function (k) {
      if (k === 'class') node.className = attrs[k];
      else if (k === 'html') node.innerHTML = attrs[k];
      else if (k.indexOf('on') === 0 && typeof attrs[k] === 'function') node.addEventListener(k.slice(2), attrs[k]);
      else node.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) {
      if (c) node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return node;
  }

  function uuid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0, v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function safeStorage(store) {
    return {
      get: function (key) {
        try { return store.getItem(key); } catch (e) { return null; }
      },
      set: function (key, val) {
        try { store.setItem(key, val); } catch (e) { /* private mode / quota — degrade silently */ }
      },
      remove: function (key) {
        try { store.removeItem(key); } catch (e) {}
      }
    };
  }
  var LOCAL = safeStorage(window.localStorage);
  var SESSION = safeStorage(window.sessionStorage);

  function readJSON(storage, key, fallback) {
    var raw = storage.get(key);
    if (!raw) return fallback;
    try { return JSON.parse(raw); } catch (e) { return fallback; }
  }
  function writeJSON(storage, key, value) {
    storage.set(key, JSON.stringify(value));
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function track(eventName, params) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        var payload = { page_location: window.location.href, page_path: window.location.pathname };
        for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) payload[k] = params[k];
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch (e) { /* tracking must never break the page */ }
  }

  function scoreRangeLabel(score) {
    if (score < 40) return '0-39';
    if (score < 60) return '40-59';
    if (score < 80) return '60-79';
    return '80-100';
  }

  function getUtmAndSource() {
    var params = new URLSearchParams(window.location.search);
    var fields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    var out = {};
    fields.forEach(function (f) {
      var v = params.get(f);
      if (v) LOCAL.set('chadmc_assessment_' + f, v); // persist across the session in case results land after a click-through
      out[f] = v || LOCAL.get('chadmc_assessment_' + f) || '';
    });
    out.landing_page = LOCAL.get('chadmc_assessment_landing_page') || window.location.pathname;
    if (!LOCAL.get('chadmc_assessment_landing_page')) LOCAL.set('chadmc_assessment_landing_page', window.location.pathname);
    out.referrer = document.referrer || '';
    return out;
  }

  /* ---------------- integration hook (Muse wires this) ---------------- */

  function queueLead(payload) {
    var queue = readJSON(LOCAL, STORAGE_KEY_LEAD_QUEUE, []);
    queue.push(payload);
    writeJSON(LOCAL, STORAGE_KEY_LEAD_QUEUE, queue);
  }

  function defaultSubmitLead(payload) {
    // Placeholder: no backend wired yet. Resolves so the UI can proceed to
    // results; the payload remains safely queued in localStorage regardless.
    return Promise.resolve({ queued: true });
  }

  window.ChadMCAssessment = window.ChadMCAssessment || {};
  window.ChadMCAssessment.integrations = window.ChadMCAssessment.integrations || {};
  if (typeof window.ChadMCAssessment.integrations.submitLead !== 'function') {
    window.ChadMCAssessment.integrations.submitLead = defaultSubmitLead;
  }

  function submitLead(payload) {
    queueLead(payload);
    try {
      document.dispatchEvent(new CustomEvent('chadmc:capital_assessment_lead_submit', { detail: payload }));
    } catch (e) {}
    return Promise.resolve(window.ChadMCAssessment.integrations.submitLead(payload)).catch(function () {
      return { queued: true, delivered: false };
    });
  }

  /* ---------------- retake / history tracking ---------------- */

  function getHistory() {
    return readJSON(LOCAL, STORAGE_KEY_HISTORY, []);
  }

  function appendHistory(result) {
    var history = getHistory();
    history.push({
      assessment_id: result.assessment_id,
      assessment_date: result.assessment_date,
      overall_score: result.overall_score
    });
    writeJSON(LOCAL, STORAGE_KEY_HISTORY, history);
    return history;
  }

  function scoreChangeSummary(history) {
    if (history.length < 2) return null;
    var first = history[0];
    var latest = history[history.length - 1];
    return {
      first_score: first.overall_score,
      first_date: first.assessment_date,
      latest_score: latest.overall_score,
      latest_date: latest.assessment_date,
      change: latest.overall_score - first.overall_score
    };
  }

  /* ---------------- app state ---------------- */

  function initState() {
    var saved = readJSON(SESSION, STORAGE_KEY_PROGRESS, null);
    if (saved && saved.answers) return saved;
    return { currentIndex: 0, answers: {} };
  }

  function saveState(state) {
    writeJSON(SESSION, STORAGE_KEY_PROGRESS, state);
  }

  function clearState() {
    SESSION.remove(STORAGE_KEY_PROGRESS);
  }

  /* ---------------- rendering ---------------- */

  function sectionLabelForCategory(categoryKey) {
    if (!categoryKey) return '';
    return CONFIG.CATEGORIES[categoryKey].label;
  }

  function ChadMCAssessmentApp(root) {
    this.root = root;
    this.state = initState();
    this.hasStarted = Object.keys(this.state.answers).length > 0;
    this.settings = readSettingsFromRoot(root);
  }

  function readSettingsFromRoot(root) {
    var scriptEl = document.getElementById('chadmc-capital-assessment-settings');
    if (!scriptEl) return {};
    try { return JSON.parse(scriptEl.textContent) || {}; } catch (e) { return {}; }
  }

  ChadMCAssessmentApp.prototype.mount = function () {
    track(CONFIG.GA4_EVENTS.view, {});
    this.renderIntro();
  };

  ChadMCAssessmentApp.prototype.clear = function () {
    this.root.innerHTML = '';
  };

  ChadMCAssessmentApp.prototype.renderIntro = function () {
    this.clear();
    var self = this;
    var s = this.settings;

    var wrap = el('div', { class: 'chadmc-assess__intro' });
    wrap.appendChild(el('h1', { class: 'chadmc-assess__headline' }, [s.headline || 'How Ready Is Your Company to Raise Capital?']));
    wrap.appendChild(el('p', { class: 'chadmc-assess__supporting' }, [s.supporting_copy || 'Assess the strength of your investment case, financing strategy, investor materials, investor targeting and execution process, then identify the gaps that deserve attention before or during your raise.']));

    var meta = el('p', { class: 'chadmc-assess__meta' }, [s.supporting_line || 'Approximately 5 minutes. 26 questions. Personalized results.']);
    wrap.appendChild(meta);

    var history = getHistory();
    if (history.length > 0) {
      var change = scoreChangeSummary(history);
      var retakeNote = el('div', { class: 'chadmc-assess__retake-note' });
      if (change) {
        retakeNote.appendChild(el('p', {}, [
          'First assessment: ' + change.first_score + '/100. Latest: ' + change.latest_score + '/100. ' +
          (change.change >= 0 ? '+' : '') + change.change + ' change.'
        ]));
      } else {
        retakeNote.appendChild(el('p', {}, ['You previously completed this assessment (' + history[history.length - 1].overall_score + '/100).']));
      }
      wrap.appendChild(retakeNote);
    }

    var cta = el('button', {
      class: 'chadmc-assess__cta global-button global-button--primary-desktop global-button--primary-mobile',
      type: 'button',
      onclick: function () {
        track(history.length > 0 ? CONFIG.GA4_EVENTS.retake : CONFIG.GA4_EVENTS.start, {});
        self.renderQuestion();
      }
    }, [s.cta_label || (history.length > 0 ? 'Retake the Assessment' : 'Assess Your Raise Readiness')]);
    wrap.appendChild(cta);

    wrap.appendChild(this.renderDisclaimer());
    this.root.appendChild(wrap);
  };

  ChadMCAssessmentApp.prototype.renderDisclaimer = function () {
    return el('p', { class: 'chadmc-assess__disclaimer' }, [CONFIG.DISCLAIMER_TEXT]);
  };

  ChadMCAssessmentApp.prototype.currentQuestion = function () {
    return CONFIG.QUESTIONS[this.state.currentIndex];
  };

  ChadMCAssessmentApp.prototype.renderProgress = function (container) {
    var total = CONFIG.QUESTIONS.length;
    var current = this.state.currentIndex + 1;
    var pct = Math.round((this.state.currentIndex / total) * 100);
    var q = this.currentQuestion();

    var progressWrap = el('div', { class: 'chadmc-assess__progress' });
    progressWrap.appendChild(el('div', { class: 'chadmc-assess__progress-track' }, [
      el('div', { class: 'chadmc-assess__progress-fill', style: 'width:' + pct + '%' })
    ]));
    var label = el('p', { class: 'chadmc-assess__progress-label' }, [
      'Question ' + current + ' of ' + total + (q.category ? ' · ' + sectionLabelForCategory(q.category) : '')
    ]);
    progressWrap.appendChild(label);
    container.appendChild(progressWrap);
  };

  ChadMCAssessmentApp.prototype.renderQuestion = function () {
    this.clear();
    var self = this;
    var q = this.currentQuestion();
    var wrap = el('div', { class: 'chadmc-assess__question-screen' });

    this.renderProgress(wrap);

    wrap.appendChild(el('h2', { class: 'chadmc-assess__question-text' }, [q.text]));
    if (q.helperText) {
      wrap.appendChild(el('p', { class: 'chadmc-assess__helper-text' }, [q.helperText]));
    }

    var optionsWrap = el('div', { class: 'chadmc-assess__options', role: 'radiogroup' });
    var selected = this.state.answers[q.id];

    q.options.forEach(function (opt) {
      var isSelected = selected === opt.value;
      var btn = el('button', {
        type: 'button',
        class: 'chadmc-assess__option' + (isSelected ? ' is-selected' : ''),
        'aria-pressed': isSelected ? 'true' : 'false',
        onclick: function () {
          self.state.answers[q.id] = opt.value;
          saveState(self.state);
          $all('.chadmc-assess__option', optionsWrap).forEach(function (b) { b.classList.remove('is-selected'); b.setAttribute('aria-pressed', 'false'); });
          btn.classList.add('is-selected');
          btn.setAttribute('aria-pressed', 'true');
          $('.chadmc-assess__next', wrap).removeAttribute('disabled');
        }
      }, [
        el('span', { class: 'chadmc-assess__option-marker' }),
        el('span', { class: 'chadmc-assess__option-label' }, [opt.label])
      ]);
      optionsWrap.appendChild(btn);
    });
    wrap.appendChild(optionsWrap);

    var nav = el('div', { class: 'chadmc-assess__nav' });
    if (this.state.currentIndex > 0) {
      nav.appendChild(el('button', {
        type: 'button',
        class: 'chadmc-assess__prev',
        onclick: function () { self.goPrev(); }
      }, ['Previous']));
    }
    var isLast = this.state.currentIndex === CONFIG.QUESTIONS.length - 1;
    var nextBtn = el('button', {
      type: 'button',
      class: 'chadmc-assess__next global-button global-button--primary-desktop global-button--primary-mobile',
      onclick: function () { self.goNext(); }
    }, [isLast ? 'See My Results' : 'Next']);
    if (!selected) nextBtn.setAttribute('disabled', 'disabled');
    nav.appendChild(nextBtn);
    wrap.appendChild(nav);

    this.root.appendChild(wrap);
  };

  ChadMCAssessmentApp.prototype.goPrev = function () {
    if (this.state.currentIndex === 0) return;
    this.state.currentIndex -= 1;
    saveState(this.state);
    this.renderQuestion();
  };

  ChadMCAssessmentApp.prototype.goNext = function () {
    var q = this.currentQuestion();
    if (!this.state.answers[q.id]) return; // guarded in UI already; defensive no-op

    var wasLastInCategory = this.isLastInCategory(this.state.currentIndex);
    if (wasLastInCategory && q.category) {
      track(CONFIG.GA4_EVENTS.sectionComplete, { section: q.category });
    }

    if (this.state.currentIndex === CONFIG.QUESTIONS.length - 1) {
      track(CONFIG.GA4_EVENTS.complete, {});
      this.finishAssessment();
      return;
    }
    this.state.currentIndex += 1;
    saveState(this.state);
    this.renderQuestion();
  };

  ChadMCAssessmentApp.prototype.isLastInCategory = function (index) {
    var q = CONFIG.QUESTIONS[index];
    var next = CONFIG.QUESTIONS[index + 1];
    return !next || next.category !== q.category;
  };

  ChadMCAssessmentApp.prototype.finishAssessment = function () {
    this.result = SCORING.evaluate(this.state.answers);
    this.renderPartialReveal();
  };

  ChadMCAssessmentApp.prototype.renderPartialReveal = function () {
    this.clear();
    var self = this;
    var r = this.result;

    var wrap = el('div', { class: 'chadmc-assess__partial' });
    wrap.appendChild(el('p', { class: 'chadmc-assess__eyebrow' }, ['YOUR ASSESSMENT IS COMPLETE']));
    wrap.appendChild(el('h2', { class: 'chadmc-assess__classification' }, [r.classification.label]));
    wrap.appendChild(el('p', { class: 'chadmc-assess__supporting' }, [
      'Your complete Capital Raise Readiness Report includes your overall score, six-category breakdown, primary constraint and recommended next steps.'
    ]));

    var form = el('form', { class: 'chadmc-assess__lead-form', novalidate: 'novalidate' });
    var firstName = el('input', { type: 'text', name: 'first_name', placeholder: 'First name', required: 'required', autocomplete: 'given-name' });
    var email = el('input', { type: 'email', name: 'email', placeholder: 'Email', required: 'required', autocomplete: 'email' });
    var company = el('input', { type: 'text', name: 'company', placeholder: 'Company (optional)', autocomplete: 'organization' });

    var errorEl = el('p', { class: 'chadmc-assess__form-error', 'aria-live': 'polite' });

    var consentLabel = el('label', { class: 'chadmc-assess__consent' }, [
      el('input', { type: 'checkbox', name: 'marketing_consent', value: 'yes' }),
      el('span', {}, [' I agree to receive my results and occasional related communications from Chad McMillan. Unsubscribe anytime.'])
    ]);

    form.appendChild(el('div', { class: 'chadmc-assess__field' }, [firstName]));
    form.appendChild(el('div', { class: 'chadmc-assess__field' }, [email]));
    form.appendChild(el('div', { class: 'chadmc-assess__field' }, [company]));
    form.appendChild(consentLabel);
    form.appendChild(errorEl);

    var submitBtn = el('button', {
      type: 'submit',
      class: 'chadmc-assess__submit global-button global-button--primary-desktop global-button--primary-mobile'
    }, ['Get My Complete Report']);
    form.appendChild(submitBtn);

    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      errorEl.textContent = '';

      if (!firstName.value.trim()) {
        errorEl.textContent = 'Please enter your first name.';
        firstName.focus();
        return;
      }
      if (!isValidEmail(email.value.trim())) {
        errorEl.textContent = 'Please enter a valid email address.';
        email.focus();
        return;
      }

      submitBtn.setAttribute('disabled', 'disabled');
      submitBtn.textContent = 'Submitting…';

      var payload = self.buildResultPayload({
        first_name: firstName.value.trim(),
        email: email.value.trim(),
        company: company.value.trim(),
        marketing_consent: $('input[name="marketing_consent"]', form).checked
      });

      track(CONFIG.GA4_EVENTS.leadSubmit, {
        readiness_stage: r.classification.key,
        primary_constraint: r.primaryConstraint,
        recommended_route: r.route,
        overall_score_range: scoreRangeLabel(r.overallScore)
      });

      submitLead(payload).then(function () {
        appendHistory(payload);
        clearState();
        self.lastPayload = payload;
        self.renderFullResults(payload);
      }).catch(function () {
        // submitLead already guarantees local persistence; proceed regardless
        // so the user is never blocked from their own results (spec: do not
        // erase assessment results on a temporary submission failure).
        appendHistory(payload);
        clearState();
        self.lastPayload = payload;
        self.renderFullResults(payload);
      });
    });

    wrap.appendChild(form);
    wrap.appendChild(this.renderDisclaimer());
    this.root.appendChild(wrap);
  };

  ChadMCAssessmentApp.prototype.buildResultPayload = function (lead) {
    var r = this.result;
    var utm = getUtmAndSource();
    var q1 = CONFIG.QUESTIONS[0];
    var q1Opt = q1.options.filter(function (o) { return o.value === this.state.answers.q1; }, this)[0];
    var companyStage = q1Opt ? q1Opt.dataValue : null;

    return {
      assessment_id: uuid(),
      assessment_version: CONFIG.ASSESSMENT_VERSION,
      assessment_date: new Date().toISOString(),

      first_name: lead.first_name,
      email: lead.email,
      company: lead.company,
      marketing_consent: lead.marketing_consent,

      company_stage: companyStage,

      overall_score: r.overallScore,
      readiness_stage: r.classification.key,

      company_readiness_score: r.categoryScores.company_readiness,
      investment_case_score: r.categoryScores.investment_case,
      financing_strategy_score: r.categoryScores.financing_strategy,
      investor_materials_score: r.categoryScores.investor_materials,
      investor_targeting_score: r.categoryScores.investor_targeting,
      raise_execution_score: r.categoryScores.raise_execution,

      primary_constraint: r.primaryConstraint,
      secondary_constraint: r.secondaryConstraint,

      recommended_route: r.route,

      answers: Object.assign({}, this.state.answers),

      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      utm_content: utm.utm_content,
      utm_term: utm.utm_term,
      landing_page: utm.landing_page,
      referrer: utm.referrer
    };
  };

  ChadMCAssessmentApp.prototype.renderFullResults = function (payload) {
    this.clear();
    var self = this;
    var r = this.result;

    track(CONFIG.GA4_EVENTS.resultsView, {
      readiness_stage: r.classification.key,
      primary_constraint: r.primaryConstraint,
      recommended_route: r.route,
      overall_score_range: scoreRangeLabel(r.overallScore)
    });

    var wrap = el('div', { class: 'chadmc-assess__results' });

    wrap.appendChild(el('p', { class: 'chadmc-assess__eyebrow' }, ['CAPITAL RAISE READINESS SCORE']));
    wrap.appendChild(el('p', { class: 'chadmc-assess__score-number' }, [String(r.overallScore) + ' / 100']));
    wrap.appendChild(el('h2', { class: 'chadmc-assess__classification' }, [r.classification.label]));
    wrap.appendChild(el('p', { class: 'chadmc-assess__supporting' }, [r.classification.description]));

    if (r.foundationPriorityTriggered) {
      wrap.appendChild(el('div', { class: 'chadmc-assess__callout' }, [el('p', {}, [r.foundationPriorityMessage])]));
    }

    var constraintsWrap = el('div', { class: 'chadmc-assess__constraints' });
    constraintsWrap.appendChild(el('div', { class: 'chadmc-assess__constraint' }, [
      el('p', { class: 'chadmc-assess__constraint-label' }, ['PRIMARY CONSTRAINT']),
      el('p', { class: 'chadmc-assess__constraint-value' }, [sectionLabelForCategory(r.primaryConstraint)])
    ]));
    constraintsWrap.appendChild(el('div', { class: 'chadmc-assess__constraint' }, [
      el('p', { class: 'chadmc-assess__constraint-label' }, ['SECONDARY CONSTRAINT']),
      el('p', { class: 'chadmc-assess__constraint-value' }, [sectionLabelForCategory(r.secondaryConstraint)])
    ]));
    wrap.appendChild(constraintsWrap);
    if (r.constraintsCloselyRelated) {
      wrap.appendChild(el('p', { class: 'chadmc-assess__helper-text' }, ['These two areas scored closely together — both deserve attention rather than treating one as clearly worse than the other.']));
    }

    var barsWrap = el('div', { class: 'chadmc-assess__bars' });
    r.categoryBreakdown.forEach(function (cat) {
      barsWrap.appendChild(el('div', { class: 'chadmc-assess__bar-row' }, [
        el('div', { class: 'chadmc-assess__bar-label' }, [cat.label]),
        el('div', { class: 'chadmc-assess__bar-track' }, [
          el('div', { class: 'chadmc-assess__bar-fill', style: 'width:' + cat.score + '%' })
        ]),
        el('div', { class: 'chadmc-assess__bar-value' }, [String(cat.score)])
      ]));
    });
    wrap.appendChild(barsWrap);

    wrap.appendChild(el('h3', { class: 'chadmc-assess__section-heading' }, ['WHAT THIS MEANS']));
    wrap.appendChild(el('p', { class: 'chadmc-assess__body-copy' }, [r.primaryConstraintDiagnosis]));

    wrap.appendChild(el('h3', { class: 'chadmc-assess__section-heading' }, ['YOUR NEXT THREE MOVES']));
    var movesWrap = el('div', { class: 'chadmc-assess__moves' });
    r.recommendations.forEach(function (move, i) {
      movesWrap.appendChild(el('div', { class: 'chadmc-assess__move' }, [
        el('p', { class: 'chadmc-assess__move-title' }, [(i + 1) + '. ' + move.title]),
        el('p', { class: 'chadmc-assess__move-body' }, [move.body])
      ]));
    });
    wrap.appendChild(movesWrap);

    wrap.appendChild(el('h3', { class: 'chadmc-assess__section-heading' }, ['YOUR COMPLETE BREAKDOWN']));
    var breakdownWrap = el('div', { class: 'chadmc-assess__breakdown' });
    r.categoryBreakdown.forEach(function (cat) {
      breakdownWrap.appendChild(el('div', { class: 'chadmc-assess__breakdown-item' }, [
        el('p', { class: 'chadmc-assess__breakdown-title' }, [cat.label + ' — ' + cat.score]),
        el('p', { class: 'chadmc-assess__breakdown-body' }, [cat.diagnosis])
      ]));
    });
    wrap.appendChild(breakdownWrap);

    /* Commercial CTA — appears only after the diagnostic content above, per spec section 11. */
    var routeCopy = r.routeCopy;
    var ctaWrap = el('div', { class: 'chadmc-assess__cta-section' });
    ctaWrap.appendChild(el('h3', { class: 'chadmc-assess__section-heading' }, [routeCopy.heading]));
    ctaWrap.appendChild(el('p', { class: 'chadmc-assess__body-copy' }, [routeCopy.body]));
    var ctaLink = el('a', {
      href: routeCopy.url,
      class: 'chadmc-assess__cta-link global-button global-button--primary-desktop global-button--primary-mobile',
      onclick: function () {
        track(r.route === 'strategy_session' ? CONFIG.GA4_EVENTS.strategyClick : CONFIG.GA4_EVENTS.courseClick, {
          recommended_route: r.route
        });
      }
    }, [routeCopy.cta + (routeCopy.priceDisplay ? ' — ' + routeCopy.priceDisplay : '')]);
    ctaWrap.appendChild(ctaLink);

    if (routeCopy.secondaryOffer && CONFIG.ROUTE_COPY[routeCopy.secondaryOffer]) {
      var secondary = CONFIG.ROUTE_COPY[routeCopy.secondaryOffer];
      ctaWrap.appendChild(el('p', { class: 'chadmc-assess__secondary-offer' }, [
        'Prefer a self-directed option? ',
        el('a', {
          href: secondary.url,
          onclick: function () { track(CONFIG.GA4_EVENTS.courseClick, { recommended_route: routeCopy.secondaryOffer, context: 'secondary_offer' }); }
        }, [secondary.cta])
      ]));
    }
    wrap.appendChild(ctaWrap);

    wrap.appendChild(this.renderDisclaimer());
    this.root.appendChild(wrap);
  };

  /* ---------------- lead-queue retry (best-effort, no backend yet) ---------------- */

  function retryQueuedLeads() {
    var queue = readJSON(LOCAL, STORAGE_KEY_LEAD_QUEUE, []);
    if (!queue.length) return;
    if (window.ChadMCAssessment.integrations.submitLead === defaultSubmitLead) return; // nothing real to retry against yet
    queue.forEach(function (payload) {
      Promise.resolve(window.ChadMCAssessment.integrations.submitLead(payload)).catch(function () {});
    });
  }

  /* ---------------- boot ---------------- */

  function boot() {
    var root = document.getElementById('chadmc-capital-assessment');
    if (!root) return;
    var app = new ChadMCAssessmentApp(root);
    window.ChadMCAssessment.instance = app;
    app.mount();
    retryQueuedLeads();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
